import { useState } from 'react'

import { Box, Divider, Form, Link, Tabs, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  JournalEntryFragment,
  JournalLineReconciliationFragment,
  TaxTypeFragment,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { JournalEntryTab } from 'modules/finance/components/JournalsEntry/JournalEntryTab'
import { JournalDraftEntryTab } from 'modules/finance/components/JournalsEntry/JournalEntryTab/JournalDraftEntry'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { isInvoiceEligibleToDoAccounting } from 'modules/finance/lib/invoices'
import { getSoftware } from 'modules/software_management/lib/software'

import { BasicInfoTab } from './BasicInfoTab'
import { CategorizationTab } from './CategorizationTab'
import { FooterActions } from './_FooterActions'
import { buildDisabledFields } from './_utils'
import { InvoiceForm } from './types'
import { InvoiceType, LegalEntity } from './types'

const TABS = {
  BASIC_INFORMATION: 'basic-information',
  RESOURCES: 'resources',
  ACCOUNTING: 'accounting',
}

export interface FormProps<T extends InvoiceType> {
  invoice: T
  legalEntities: LegalEntity[]
  showReconciliationSection: boolean
  showExpense: boolean
  showSoftware: boolean
  showProjects: boolean
  showPurchaseOrder: boolean
  showSendToPayButton: boolean
  taxTypes?: TaxTypeFragment[]
  form: FormikProps<InvoiceForm>
  canManageJournalEntries: boolean
  canReconcileWithJournalLines: boolean
  canReconcileWithFinancialDocuments: boolean
  journalLines: JournalLineReconciliationFragment[]
  journalEntries: JournalEntryFragment[]
}

export const InvoiceDetail = <T extends InvoiceType>({
  invoice,
  showReconciliationSection,
  canManageJournalEntries,
  showSoftware,
  showProjects,
  showPurchaseOrder,
  showExpense,
  legalEntities,
  showSendToPayButton,
  taxTypes,
  form,
  canReconcileWithJournalLines,
  canReconcileWithFinancialDocuments,
  journalLines,
  journalEntries,
}: FormProps<T>) => {
  const [tab, setTab] = useState(TABS.BASIC_INFORMATION)
  const { navigation } = useNavigation()
  const disabledFields = buildDisabledFields(invoice)
  const flatJournalEntries = (journalEntries || [])
    .flatMap((entry) => (entry.reversalEntry ? [entry, entry.reversalEntry] : [entry]))
    .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true }))
  const legalEntityId = form.values.legalEntity?.id || journalEntries[0]?.legalEntity?.id
  const duplicateInvoice = invoice.duplicateFinancialDocument

  const expense = invoice.expensesFinancialDocumentAssociation?.expense
  const canSeeCategorizationTab =
    showSoftware ||
    showPurchaseOrder ||
    showProjects ||
    (showExpense && expense) /* Expense cannot be selected. We just show it if it exists */

  const canSeeAccountingTab =
    canManageJournalEntries &&
    isInvoiceEligibleToDoAccounting(invoice) &&
    legalEntityId &&
    (invoice.journalDraftEntry || (journalEntries && journalEntries.length > 0))

  const tabs = [
    {
      tabId: TABS.BASIC_INFORMATION,
      label: i18n.t('finance.invoices.detail.tabs.basic_information'),
    },
    canSeeCategorizationTab && {
      tabId: TABS.RESOURCES,
      label: i18n.t('finance.invoices.detail.tabs.categorization'),
    },
    canSeeAccountingTab && {
      tabId: TABS.ACCOUNTING,
      label: i18n.t('finance.invoices.detail.tabs.accounting'),
    },
  ].filter(Boolean)

  const purchaseOrderLabel =
    invoice.purchaseOrderFinancialDocumentAssociation?.purchaseOrder?.formattedPoNumber
  const softwareLabel = invoice.softwareSetupFinancialDocumentAssociation?.softwareSetup
    ? getSoftware(invoice.softwareSetupFinancialDocumentAssociation?.softwareSetup).name
    : undefined

  const project = invoice.projectsFinancialDocumentAssociation?.financialProject
  const subproject = invoice.subprojectsFinancialDocumentAssociation?.subproject
  const associatedResourceLabel = purchaseOrderLabel || softwareLabel

  return (
    <Form padding="s0" notRounded>
      <Box gap="s16" paddingX="s24" paddingBottom="s96" paddingTop="s16">
        {!!duplicateInvoice && (
          <Callout
            type="warning"
            title={i18n.t('finance.invoices.callouts.duplicate_invoice.title')}
            description={i18n.t(
              'finance.invoices.callouts.duplicate_invoice.description_with_vendor',
              {
                invoiceNumber: duplicateInvoice.documentNumber,
                invoiceDate: dateBackendDateTimeToUI(
                  duplicateInvoice.documentDate || invoice.createdAt
                ),
                invoiceVendor: contactFullName(invoice.vendor),
              }
            )}
            link={
              <Link.Router
                to={navigation.finance.invoices.list.invoiceId.fullPath({
                  invoiceId: duplicateInvoice.id,
                })}
              >
                <Text size="200" color="grey1000" weight="medium" underlined>
                  {i18n.t('finance.invoices.callouts.duplicate_invoice.link')}
                </Text>
              </Link.Router>
            }
          />
        )}
        <Tabs tabsAlign="flexStart" onTabClicked={(tabId: string) => setTab(tabId)} currentId={tab}>
          {tabs.map(({ tabId, label }) => (
            <Tabs.Tab key={tabId} id={tabId} label={label}>
              {tabId === TABS.BASIC_INFORMATION && (
                <BasicInfoTab
                  invoice={invoice}
                  form={form}
                  defaultValues={form.initialValues}
                  disabledFields={disabledFields}
                  legalEntities={legalEntities}
                  showReconciliationSection={showReconciliationSection}
                  canManageJournalEntries={canManageJournalEntries}
                  taxTypes={taxTypes}
                  canReconcileWithJournalLines={canReconcileWithJournalLines}
                  canReconcileWithFinancialDocuments={canReconcileWithFinancialDocuments}
                  journalLines={journalLines}
                />
              )}
              {tabId === TABS.RESOURCES && (
                <CategorizationTab
                  expense={expense}
                  form={form}
                  disabledFields={disabledFields}
                  showExpense={showExpense}
                  showSoftware={showSoftware}
                  showPurchaseOrder={showPurchaseOrder}
                  showProjects={showProjects}
                  selectedResourceLabel={associatedResourceLabel}
                  selectedSoftwareObject={
                    invoice.softwareSetupFinancialDocumentAssociation?.softwareSetup
                  }
                  selectedPurchaseOrderObject={
                    invoice.purchaseOrderFinancialDocumentAssociation?.purchaseOrder
                  }
                  selectedProjectObject={project}
                  selectedSubprojectObject={subproject}
                />
              )}
              {legalEntityId &&
                form.values.legalEntity &&
                invoice.journalDraftEntry &&
                invoice.currency &&
                tabId === TABS.ACCOUNTING && (
                  <Box padding={'s16'}>
                    <JournalDraftEntryTab
                      journalDraftEntry={invoice.journalDraftEntry}
                      legalEntity={form.values.legalEntity}
                      currency={invoice.currency}
                    />
                    {flatJournalEntries.length > 0 && <Divider />}
                  </Box>
                )}

              {legalEntityId && flatJournalEntries.length > 0 && tabId === TABS.ACCOUNTING && (
                <Box paddingTop={'s16'} gap="s16" flexDirection="column">
                  {flatJournalEntries.map(
                    (journalEntry, index) =>
                      journalEntry &&
                      journalEntry.legalEntity &&
                      invoice.currency && (
                        <Box key={journalEntry.id} flexDirection="column" gap="s16">
                          {index > 0 && <Divider />}
                          <JournalEntryTab
                            journalEntry={journalEntry}
                            legalEntity={journalEntry.legalEntity}
                            currency={invoice.currency}
                          />
                        </Box>
                      )
                  )}
                </Box>
              )}
            </Tabs.Tab>
          ))}
        </Tabs>
      </Box>
      <FooterActions
        form={form}
        disableActions={!form.isValid || form.isSubmitting}
        invoice={invoice}
        showSendToPayButton={showSendToPayButton}
      />
    </Form>
  )
}
